/**
 * clickhouse-grants.ts — the grants a ClickHouse sync user needs (grants reference 2.1), rendered
 * once for every consumer: the Setup SQL panel customers copy, and the e2e fixture users that must
 * hold exactly what customers grant.
 *
 * Every identifier is checked against the account-field name rule and then backtick-quoted, so no
 * typed text reaches a statement unquoted.
 */

import { defaultRudderSchema } from './clickhouse-form.js';

const CLICKHOUSE_NAME = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
const RESERVED_DATABASES = ['default', 'system', 'information_schema'];
export const WORKING_DATABASE_RULE =
  'The database for sync state must differ from your database, default, system and information_schema.';

export type ClickHouseGrantParams = { database: string; user: string; rudderSchema?: string | undefined };

type Scope = 'customer' | 'working' | 'working.sync_log';

// `why` is the one comment line the script prints above the grant.
const GRANTS = [
  {
    name: 'customer_read',
    privileges: ['SELECT'],
    scope: 'customer',
    why: 'Lets this user read your database. The user cannot change your data.',
  },
  {
    name: 'working_write',
    privileges: ['SELECT', 'INSERT', 'CREATE TABLE', 'DROP TABLE'],
    scope: 'working',
    why: 'Lets this user create, write, read and drop tables in the working database, which holds the sync state.',
  },
  {
    // Granted whatever the connection's sync-log retention, so turning retention on later needs no
    // new grant. ClickHouse accepts the grant before the connector creates sync_log, and keeps it.
    name: 'sync_log_delete',
    privileges: ['ALTER DELETE'],
    scope: 'working.sync_log',
    why: 'Lets this user delete old rows from the sync log in the working database. The user cannot delete your data.',
  },
] as const satisfies ReadonlyArray<{ name: string; privileges: readonly string[]; scope: Scope; why: string }>;

export type ClickHouseGrantName = (typeof GRANTS)[number]['name'];

export type RenderedClickHouseGrant = {
  name: ClickHouseGrantName;
  /** One line, no trailing semicolon. */
  statement: string;
  /** One `PRIVILEGE ON scope` key per privilege, identifiers unquoted, the form SHOW GRANTS normalises to. */
  keys: string[];
};

function checkedName(value: string, label: string): string {
  const trimmed = value.trim();
  if (!CLICKHOUSE_NAME.test(trimmed)) {
    throw new Error(
      `${label}: use letters, digits and underscores, start with a letter or underscore, at most 128 characters`,
    );
  }
  return trimmed;
}

type Names = { database: string; working: string; user: string };

function checkedNames(params: ClickHouseGrantParams): Names {
  const rudderSchema = params.rudderSchema ?? defaultRudderSchema;
  const working = checkedName(rudderSchema, 'The database for sync state');
  const database = checkedName(params.database, 'Database');
  // The working database gets write grants, so it must never name the customer database or a system
  // one. Same rule as config-backend's account validation, in Lookout's words.
  const workingLower = rudderSchema.trim().toLowerCase();
  if ([params.database.trim().toLowerCase(), ...RESERVED_DATABASES].includes(workingLower)) {
    throw new Error(WORKING_DATABASE_RULE);
  }
  return { database, working, user: checkedName(params.user, 'Username') };
}

const quoted = (name: string): string => `\`${name}\``;

function scopeOf(scope: Scope, names: Names, quote: (name: string) => string): string {
  if (scope === 'customer') return `${quote(names.database)}.*`;
  if (scope === 'working') return `${quote(names.working)}.*`;
  return `${quote(names.working)}.${quote('sync_log')}`;
}

function grantText(grant: (typeof GRANTS)[number], names: Names, separator: string): string {
  return `GRANT ${grant.privileges.join(', ')}${separator}ON ${scopeOf(grant.scope, names, quoted)}${separator}TO ${quoted(names.user)}`;
}

export function clickHouseGrants(params: ClickHouseGrantParams): RenderedClickHouseGrant[] {
  const names = checkedNames(params);
  return GRANTS.map((grant) => ({
    name: grant.name,
    statement: grantText(grant, names, ' '),
    keys: grant.privileges.map((p) => `${p} ON ${scopeOf(grant.scope, names, (n) => n)}`),
  }));
}

/** Database-wide grants print on three lines, the rest on one. */
const MULTILINE: ReadonlySet<Scope> = new Set(['customer', 'working']);

/**
 * The Setup SQL panel script. The customer provisions the user and its password; this script only
 * grants. Each grant has one `--` line above it that says why the user needs it.
 */
export function clickHouseSetupScript(params: ClickHouseGrantParams): string {
  const names = checkedNames(params);
  const grants = GRANTS.map(
    (grant) => `-- ${grant.why}\n${grantText(grant, names, MULTILINE.has(grant.scope) ? '\n' : ' ')};\n`,
  );
  return `-- Step 1: create the user and its password in ClickHouse first. This script does not create the user.
-- Step 2: run this script as a ClickHouse administrator.

-- The administrator creates the database that stores sync state.
CREATE DATABASE IF NOT EXISTS ${quoted(names.working)};

${grants.join('\n')}`;
}
